import dayjs from 'dayjs'
import { makeAutoObservable, runInAction } from 'mobx'

import { DISK_ROLE_LABEL, DISK_ROLE_RANK } from '../Contracts/DiskShare'
import { diskErrorText } from '../Helpers/diskErrors'
import { CreateDiskShareTask } from '../Tasks/CreateDiskShareTask'
import { LoadDiskNodeSharesTask } from '../Tasks/LoadDiskNodeSharesTask'
import { RemoveDiskShareTask } from '../Tasks/RemoveDiskShareTask'
import { SearchDiskPrincipalsTask } from '../Tasks/SearchDiskPrincipalsTask'
import { UpdateDiskShareTask } from '../Tasks/UpdateDiskShareTask'

import type { DiskRole } from '../Contracts/DiskNode'
import type { DiskNodeRef, DiskNodeShares, DiskPrincipalOption, DiskShareRow } from '../Contracts/DiskShare'
import type { DiskActionResult } from './DiskBrowserStore'

const SEARCH_DELAY_MS = 300

/** Строк нового доступу: без строку, тиждень, місяць або до дати. */
export type DiskExpiryChoice = 'none' | 'week' | 'month' | 'date'

/** Чому роль недоступна в селекті; `null` — доступна. */
export type DiskRoleBlock = string | null

/** Успадкована роль адресата й звідки вона. */
export interface DiskInheritedFor {
    role: DiskRole
    source: string
}

/** Поля прибраного рядка, з яких «Скасувати» надає той самий доступ заново. */
const principalOf = (row: DiskShareRow): string | null =>
    row.principalType === 'role' ? row.principal.code : row.principalType === 'company' ? null : row.principal.uuid

/** `2026-11-30T23:59:59+02:00` → `2026-11-30`, якщо строк ще в майбутньому; інакше — без строку. */
const futureDate = (value: string | null): string | null =>
    value !== null && dayjs(value).isAfter(dayjs()) ? dayjs(value).format('YYYY-MM-DD') : null

/**
 * Модалка «Поділитися» (`DISK-005` §3.6.2, фаза 2): хто має доступ до вузла
 * (учасники простору, успадковані з папок, надані тут), пошук адресатів і
 * зміни по рядку — без «Зберегти» для всього набору: двоє менеджерів, що
 * правлять одночасно, не затирають одне одного.
 *
 * - Ролі вищі за мою (`me.maxGrantRole`) і нижчі за успадковану вибраними
 *   адресатами — вимкнені з поясненням (D-7, D-8; бек перевіряє те саме).
 * - Прибраний рядок бек віддає цілим — «Скасувати» надає його заново.
 * - Модалка може перейти до папки-предка, від якої успадковано доступ, і
 *   повернутися назад (`trail`).
 * - Після змін — `onChanged` власника (перечитати список: значки доступу).
 */
export class DiskShareStore {
    node: DiskNodeRef | null = null
    data: DiskNodeShares | null = null
    isLoading = false
    error: string | null = null
    /** Звідки перейшли до предка — кнопка «Назад». */
    trail: DiskNodeRef[] = []

    query = ''
    options: DiskPrincipalOption[] = []
    isSearching = false
    picked: DiskPrincipalOption[] = []
    role: DiskRole = 'viewer'
    expiry: DiskExpiryChoice = 'none'
    /** `YYYY-MM-DD` для «До дати». */
    expiryDate: string | null = null
    notify = true
    isGranting = false
    /** Рядки, над якими йде запит. */
    busy: string[] = []

    private changed = false
    private onChanged: (() => void) | null = null
    private requestId = 0
    private searchId = 0
    private searchTimer: ReturnType<typeof setTimeout> | null = null

    constructor() {
        makeAutoObservable<DiskShareStore, 'changed' | 'onChanged' | 'requestId' | 'searchId' | 'searchTimer'>(this, {
            changed: false,
            onChanged: false,
            requestId: false,
            searchId: false,
            searchTimer: false,
        })
    }

    get isOpen(): boolean {
        return this.node !== null
    }

    /** Змінювати доступи тут можна: є право `share`, і це не корінь простору (його доступ — учасники). */
    get canShare(): boolean {
        return this.data !== null && this.data.me.canShare && !this.data.node.root
    }

    open(node: DiskNodeRef, onChanged?: () => void): void {
        this.trail = []
        this.changed = false
        this.onChanged = onChanged ?? null
        this.show(node)
    }

    /** Перейти до папки, від якої успадковано доступ. */
    openAncestor(node: DiskNodeRef): void {
        if (this.node !== null) this.trail = [...this.trail, this.node]
        this.show(node)
    }

    back(): void {
        const previous = this.trail[this.trail.length - 1]
        if (!previous) return

        this.trail = this.trail.slice(0, -1)
        this.show(previous)
    }

    close(): void {
        this.requestId += 1
        this.node = null
        this.data = null
        this.error = null
        this.resetForm()

        if (this.changed) this.onChanged?.()
        this.changed = false
        this.onChanged = null
    }

    private show(node: DiskNodeRef): void {
        this.node = node
        this.data = null
        this.resetForm()
        void this.load()
        this.setQuery('')
    }

    private resetForm(): void {
        this.query = ''
        this.options = []
        this.picked = []
        this.role = 'viewer'
        this.expiry = 'none'
        this.expiryDate = null
        this.notify = true
        this.busy = []
    }

    async load(): Promise<void> {
        const node = this.node
        if (node === null) return

        const id = ++this.requestId

        this.isLoading = true
        this.error = null

        try {
            const data = await LoadDiskNodeSharesTask(node.uuid)

            runInAction(() => {
                if (id !== this.requestId) return
                this.data = data
                if (this.node !== null && data.node.name !== '') this.node = { uuid: this.node.uuid, name: data.node.name }
            })
        } catch (error) {
            runInAction(() => {
                if (id === this.requestId) this.error = diskErrorText(error, 'Не вдалося завантажити, хто має доступ')
            })
        } finally {
            runInAction(() => {
                if (id === this.requestId) this.isLoading = false
            })
        }
    }

    // ---- Пошук адресатів --------------------------------------------------

    setQuery(query: string): void {
        this.query = query

        if (this.searchTimer !== null) clearTimeout(this.searchTimer)

        this.isSearching = true
        this.searchTimer = setTimeout(() => void this.search(query.trim()), query.trim() === '' ? 0 : SEARCH_DELAY_MS)
    }

    private async search(query: string): Promise<void> {
        const id = ++this.searchId

        try {
            const options = await SearchDiskPrincipalsTask(query)

            runInAction(() => {
                if (id === this.searchId) this.options = options
            })
        } catch {
            runInAction(() => {
                if (id === this.searchId) this.options = []
            })
        } finally {
            runInAction(() => {
                if (id === this.searchId) this.isSearching = false
            })
        }
    }

    /** Адресати, яких ще можна вибрати: без вибраних і без тих, у кого доступ уже надано тут. */
    get availableOptions(): DiskPrincipalOption[] {
        const taken = new Set([
            ...this.picked.map((option) => option.principalKey),
            ...(this.data?.direct ?? []).map((row) => row.principalKey),
        ])

        return this.options.filter((option) => !taken.has(option.principalKey))
    }

    setPicked(keys: string[]): void {
        const known = new Map([...this.picked, ...this.options].map((option) => [option.principalKey, option]))

        this.picked = keys.map((key) => known.get(key)).filter((option): option is DiskPrincipalOption => !!option)

        // Роль, нижча за успадковану вибраними, — одразу піднімаємо до неї
        const floor = this.pickedFloor
        if (floor !== null && DISK_ROLE_RANK[this.role] < DISK_ROLE_RANK[floor.role]) this.role = floor.role
    }

    setRole(role: DiskRole): void {
        this.role = role
    }

    setExpiry(expiry: DiskExpiryChoice): void {
        this.expiry = expiry
        if (expiry !== 'date') this.expiryDate = null
    }

    setExpiryDate(date: string | null): void {
        this.expiryDate = date
    }

    setNotify(notify: boolean): void {
        this.notify = notify
    }

    /** Чи вибрано «Уся компанія» — їй сповіщення не надсилаються. */
    get pickedCompany(): boolean {
        return this.picked.some((option) => option.principalType === 'company')
    }

    get pickedOnlyCompany(): boolean {
        return this.picked.length > 0 && this.picked.every((option) => option.principalType === 'company')
    }

    // ---- Ролі -------------------------------------------------------------

    /**
     * Роль, яку адресат уже має не через цей вузол: учасник простору чи
     * доступ на папці-предку (D-8 — нижчу видати не можна).
     */
    inheritedFor(key: string): DiskInheritedFor | null {
        const data = this.data
        if (data === null) return null

        let best: DiskInheritedFor | null = null
        const consider = (role: DiskRole, source: string): void => {
            if (best === null || DISK_ROLE_RANK[role] > DISK_ROLE_RANK[best.role]) best = { role, source }
        }

        for (const member of data.storageMembers) {
            if (member.principalKey === key) consider(member.role, `простору «${data.storage.title}»`)
        }

        for (const row of data.inherited) {
            if (row.principalKey === key) consider(row.role, row.fromNode ? `папки «${row.fromNode.name}»` : 'папки вище')
        }

        return best
    }

    /** Найвища успадкована роль серед вибраних адресатів — нижче за неї видати не можна. */
    get pickedFloor(): DiskInheritedFor | null {
        let floor: DiskInheritedFor | null = null

        for (const option of this.picked) {
            const inherited = this.inheritedFor(option.principalKey)
            if (inherited !== null && (floor === null || DISK_ROLE_RANK[inherited.role] > DISK_ROLE_RANK[floor.role])) {
                floor = inherited
            }
        }

        return floor
    }

    /**
     * Чому роль не можна вибрати: вища за мою (редактор видає не вище за
     * себе) чи нижча за успадковану (`floor`). `null` — можна.
     */
    roleBlock(role: DiskRole, floor: DiskInheritedFor | null): DiskRoleBlock {
        const max = this.data?.me.maxGrantRole ?? null

        if (max === null) return 'Ви не можете змінювати доступ до цього елемента'
        if (DISK_ROLE_RANK[role] > DISK_ROLE_RANK[max]) {
            return `Вища за вашу роль «${DISK_ROLE_LABEL[max]}»: видати її ви не можете`
        }
        if (floor !== null && DISK_ROLE_RANK[role] < DISK_ROLE_RANK[floor.role]) {
            return `Адресат уже має роль «${DISK_ROLE_LABEL[floor.role]}» від ${floor.source}: нижча нічого не змінить`
        }

        return null
    }

    /** Рядок, який я можу змінювати: редактор не чіпає доступ із роллю вищою за свою. */
    canTouch(row: DiskShareRow): boolean {
        const max = this.data?.me.maxGrantRole ?? null

        return this.canShare && max !== null && (max === 'manager' || DISK_ROLE_RANK[row.role] <= DISK_ROLE_RANK[max])
    }

    isBusy(uuid: string): boolean {
        return this.busy.includes(uuid)
    }

    // ---- Зміни ------------------------------------------------------------

    /** Строк нового доступу для `POST`: `YYYY-MM-DD` (до кінця дня) або `null`. */
    get expiresAt(): string | null {
        if (this.expiry === 'week') return dayjs().add(7, 'day').format('YYYY-MM-DD')
        if (this.expiry === 'month') return dayjs().add(30, 'day').format('YYYY-MM-DD')
        if (this.expiry === 'date') return this.expiryDate

        return null
    }

    get canGrant(): boolean {
        return (
            this.canShare &&
            this.picked.length > 0 &&
            !this.isGranting &&
            !(this.expiry === 'date' && this.expiryDate === null) &&
            this.roleBlock(this.role, this.pickedFloor) === null
        )
    }

    /** «Надати доступ»: по рядку на кожного вибраного адресата. */
    async grant(): Promise<{ ok: true; count: number } | { ok: false; error: string }> {
        const node = this.node
        if (node === null || !this.canGrant) return { ok: false, error: 'Виберіть, кому надати доступ' }

        this.isGranting = true

        const errors: string[] = []
        let count = 0
        const granted: string[] = []

        for (const option of this.picked) {
            try {
                await CreateDiskShareTask(node.uuid, {
                    principalType: option.principalType,
                    principal: option.principal,
                    role: this.role,
                    expiresAt: this.expiresAt,
                    notify: this.notify && option.principalType !== 'company',
                })
                count += 1
                granted.push(option.principalKey)
            } catch (error) {
                errors.push(`${option.title}: ${diskErrorText(error, 'не вдалося надати доступ')}`)
            }
        }

        runInAction(() => {
            this.isGranting = false
            // Невдалі лишаються вибраними — їх можна виправити й надати ще раз
            this.picked = this.picked.filter((option) => !granted.includes(option.principalKey))
            if (count > 0) this.changed = true
        })

        if (count > 0) await this.load()

        return errors.length > 0 ? { ok: false, error: errors.join(' ') } : { ok: true, count }
    }

    private async patch(row: DiskShareRow, patch: { role?: DiskRole; expiresAt?: string | null }, fallback: string): Promise<DiskActionResult> {
        this.busy = [...this.busy, row.uuid]

        try {
            await UpdateDiskShareTask(row.uuid, patch)
            runInAction(() => {
                this.changed = true
            })
            await this.load()

            return { ok: true }
        } catch (error) {
            return { ok: false, error: diskErrorText(error, fallback) }
        } finally {
            runInAction(() => {
                this.busy = this.busy.filter((uuid) => uuid !== row.uuid)
            })
        }
    }

    changeRole(row: DiskShareRow, role: DiskRole): Promise<DiskActionResult> {
        return this.patch(row, { role }, 'Не вдалося змінити роль')
    }

    /** `date` — `YYYY-MM-DD` (до кінця дня); `null` — без строку. */
    changeExpiry(row: DiskShareRow, date: string | null): Promise<DiskActionResult> {
        return this.patch(row, { expiresAt: date }, 'Не вдалося змінити строк')
    }

    /** Прибрати рядок; бек віддає його цілим — для «Скасувати». */
    async remove(row: DiskShareRow): Promise<{ ok: true; removed: DiskShareRow } | { ok: false; error: string }> {
        this.busy = [...this.busy, row.uuid]

        try {
            const answer = await RemoveDiskShareTask(row.uuid)
            // Для «Скасувати» беремо адресата з рядка, який бачили, якщо відповідь не про нього
            const removed = answer.principalKey === row.principalKey ? answer : row
            runInAction(() => {
                this.changed = true
            })
            await this.load()

            return { ok: true, removed }
        } catch (error) {
            return { ok: false, error: diskErrorText(error, 'Не вдалося прибрати доступ') }
        } finally {
            runInAction(() => {
                this.busy = this.busy.filter((uuid) => uuid !== row.uuid)
            })
        }
    }

    /** «Скасувати» після «Прибрати»: той самий адресат, роль і строк — без повторного сповіщення. */
    async undoRemove(nodeUuid: string, removed: DiskShareRow): Promise<DiskActionResult> {
        // Без ключа адресата не знаємо, кому повертати, — нічого не надаємо навмання
        if (removed.principalKey === '') return { ok: false, error: 'Не вдалося повернути доступ: невідомий адресат' }

        try {
            await CreateDiskShareTask(nodeUuid, {
                principalType: removed.principalType,
                principal: principalOf(removed),
                role: removed.role,
                expiresAt: futureDate(removed.expiresAt),
                notify: false,
            })
            runInAction(() => {
                this.changed = true
            })
            if (this.node?.uuid === nodeUuid) await this.load()

            return { ok: true }
        } catch (error) {
            return { ok: false, error: diskErrorText(error, 'Не вдалося повернути доступ') }
        }
    }
}

export const diskShareStore = new DiskShareStore()
